/**
 * /w/:slug/app/exceptions — Risk acceptances: ONE canonical view (V98-5-5 /
 * D-98-12) over the three v9.6 models — accepted vulnerabilities (FedRAMP
 * accepted weaknesses), accepted security risks and register exceptions.
 *
 * V98-5 review:
 *   T5-H6  sorted by urgency (no expiry / over the cap first, then days
 *          left); the subtitle is one urgency line; the filters carry counts;
 *          "No expiry set" is red with "Set expiry…" (not a renewal).
 *   T5-B4  row titles open the source record (finding, register item);
 *          the last term reads "Last term" (red) and its way forward is a
 *          filled "Open finding" / "Open register item" (or "Re-assess…" for
 *          a security risk) — never a dead end.
 *   T5-H8  rows name who renews ("Dana renews · Ask Dana to renew"); the
 *          deep link /exceptions?acceptance=<key> scrolls to, highlights and
 *          focuses that row's action.
 *   T5-B5  the urgency count is the server's one `needs_decision` flag.
 *
 * D-106-20: a risk accepted on a SOC 2 / ISO 42001 Not met point is a register
 * row too; it reads as its origin ("SOC 2 · CC6.1-POF-05") and links back to
 * the point on the program's Prepare tab.
 *
 * Reads: GET /acceptances (any member). Writes: POST /acceptances/:source/:id/renew
 * (owner/admin, never the risk owner); PUT /security-risks/:id (re-assess:
 * owner/admin/editor; the server decides).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea } from '@aigovops/design-system';
import type { CanonicalAcceptance } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import { listWorkspaceMembers } from '../lib/api';
import { getSecurityRisk, listAcceptances, reassessSecurityRisk, renewAcceptance } from '../lib/cert-ops-api';
import { fmtDate } from '../lib/cert-ops-model';
import { objectiveAnchor } from '../lib/program-screens-model';
import { firstName, nameOf } from '../lib/today-model';
import { ActionDialog, Chip, domId, errorText, useAnnounce, useFocusAfter } from '../components/today/shared';
import { AskAdmin } from '../components/AskAdmin';
import { useNames } from './Today';
import './today.css';
import './cert-ops.css';

const SOURCE_LABEL: Record<CanonicalAcceptance['source'], string> = {
  accepted_weakness: 'Accepted vulnerability',
  security_risk: 'Accepted risk',
  risk_acceptance: 'Register exception',
};

type Filter = 'all' | 'decide' | 'expired';
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Urgency order (T5-H6): no expiry / over the cap first, then past expiry, then days left. */
function urgency(r: CanonicalAcceptance): number {
  if (r.flags.includes('no_expiry') || r.flags.includes('over_cap')) return -2_000_000;
  if (r.effective_status === 'expired') return -1_000_000 + (r.days_left ?? 0);
  return r.days_left ?? 1_000_000;
}

export function Exceptions() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const canRenew = role === 'owner' || role === 'admin';
  const qc = useQueryClient();
  const names = useNames(slug);
  const [sp] = useSearchParams();
  const target = sp.get('acceptance');
  const [announceNode, announce] = useAnnounce();
  const focusLater = useFocusAfter();
  const [filter, setFilter] = useState<Filter>('all');
  const [renewing, setRenewing] = useState<CanonicalAcceptance | null>(null);
  const [reassessing, setReassessing] = useState<CanonicalAcceptance | null>(null);
  const q = useQuery({ queryKey: ['acceptances', slug], queryFn: () => listAcceptances(slug), retry: false });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  const emailOf = (u: string | null | undefined) => (members.data?.members ?? []).find((m) => m.user_id === u)?.email ?? null;
  const me = useAuth().user?.id ?? '';

  const all = useMemo(() => [...(q.data?.acceptances ?? [])].sort((a, b) => urgency(a) - urgency(b) || a.key.localeCompare(b.key)), [q.data]);
  const rows = useMemo(() => (filter === 'decide' ? all.filter((r) => r.needs_decision) : filter === 'expired' ? all.filter((r) => r.effective_status === 'expired') : all), [all, filter]);
  const s = q.data?.summary;

  // T5-H8: a deep link lands on the row — scrolled to, highlighted, its action focused.
  useEffect(() => {
    if (!target || !q.data) return;
    if (!all.some((r) => r.key === target)) return;
    if (filter !== 'all' && !rows.some((r) => r.key === target)) { setFilter('all'); return; }
    const row = document.getElementById(domId('acc', target));
    if (!row) return;
    row.scrollIntoView?.({ block: 'center' });
    (row.querySelector<HTMLElement>('[data-primary-action]') ?? row).focus();
  }, [target, q.data, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const urgencyLine = s ? (() => {
    const parts: string[] = [];
    const breaks = s.no_expiry + s.over_cap;
    if (breaks) parts.push(`${breaks} ${breaks === 1 ? 'breaks' : 'break'} the ${q.data!.rules.max_term_days}-day cap`);
    if (s.expired) parts.push(`${s.expired} past expiry`);
    if (s.expiring_soon) parts.push(s.expiring_soon === 1 && s.soonest_days !== null ? `1 expires in ${plural(s.soonest_days, 'day', 'days')}` : `${s.expiring_soon} expire within 14 days`);
    return parts.length ? parts.join(' · ') : 'Nothing needs a decision';
  })() : undefined;
  const afterWrite = (key: string) => {
    const i = rows.findIndex((r) => r.key === key);
    const next = rows[i + 1] ?? rows[i - 1];
    focusLater(next ? domId('acc', next.key) : 'acc-list-h');
  };

  return (
    <div className="gp-item">
      {announceNode}
      <PageHeader variant="app" title="Risk acceptances" headingId="acc-h1" subtitle={urgencyLine} />
      {q.isLoading ? <Spinner label="Loading acceptances" />
        : q.error || !q.data ? <Alert variant="error"><p>Acceptances could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
          : (
            <>
              <ul className="gp-filter" aria-label="Show">
                {([['all', 'All', all.length], ['decide', 'Needs a decision', s!.needs_decision], ['expired', 'Past expiry', s!.expired]] as const).map(([f, label, n]) => (
                  <li key={f}><button type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>{`${label} ${n}`}</button></li>
                ))}
              </ul>
              <h2 id="acc-list-h" className="gp-sr-only" tabIndex={-1}>{`${plural(rows.length, 'acceptance', 'acceptances')} shown`}</h2>
              <p role="status" className="gp-sr-only">{`${plural(rows.length, 'acceptance', 'acceptances')} shown`}</p>
              {rows.length === 0 ? (
                <EmptyState title={filter === 'all' ? 'No risk acceptances' : 'Nothing here'}
                  description={filter === 'all' ? 'Accept a vulnerability from its finding, or a risk from the risk register.' : 'Every acceptance in force is outside this filter.'}
                  action={filter === 'all' ? <Link className="gp-linkbtn" to={`${prefix}/findings`}>Open findings</Link> : <Button onClick={() => setFilter('all')}>Show all</Button>} />
              ) : (
                <ul className="gp-owed-list" aria-labelledby="acc-list-h" data-part="acceptances">
                  {rows.map((r) => <AcceptanceRow key={r.key} r={r} prefix={prefix} slug={slug} names={names} me={me} canRenew={canRenew} focused={r.key === target}
                    renewerEmail={emailOf(r.renewer_user_id)} onRenew={() => setRenewing(r)} onReassess={() => setReassessing(r)} />)}
                </ul>
              )}
            </>
          )}
      <RenewDialog slug={slug} acceptance={renewing} maxDays={q.data?.rules.max_term_days ?? 90} onClose={() => setRenewing(null)}
        onDone={async (m, key) => { setRenewing(null); await qc.invalidateQueries({ queryKey: ['acceptances', slug] }); afterWrite(key); announce(m); }} />
      <ReassessDialog slug={slug} acceptance={reassessing} onClose={() => setReassessing(null)}
        onDone={async (m, key) => { setReassessing(null); await qc.invalidateQueries({ queryKey: ['acceptances', slug] }); afterWrite(key); announce(m); }} />
    </div>
  );
}

function sourceLink(r: CanonicalAcceptance, prefix: string): { to: string; label: string } | null {
  // D-106-20: back to the point it was accepted on.
  if (r.origin) return { to: `${prefix}/programs/${encodeURIComponent(r.origin.program)}/prepare#${objectiveAnchor(r.origin.program, r.origin.point_label)}`, label: 'Open the point' };
  if (r.subject.kind === 'finding') return { to: `${prefix}/findings?finding=${encodeURIComponent(r.subject.id)}`, label: 'Open finding' };
  if (r.subject.kind === 'register_item') return { to: `${prefix}/register/${encodeURIComponent(r.subject.id)}`, label: 'Open register item' };
  return null;
}

function AcceptanceRow({ r, prefix, slug, names, me, canRenew, focused, renewerEmail, onRenew, onReassess }: {
  r: CanonicalAcceptance; prefix: string; slug: string; names: ReadonlyMap<string, string>; me: string; canRenew: boolean; focused: boolean;
  renewerEmail: string | null; onRenew: () => void; onReassess: () => void;
}) {
  const noExpiry = r.flags.includes('no_expiry');
  const expiry = r.effective_status === 'expired' ? { text: `Expired ${fmtDate(r.expires_at)}`, tone: 'red' as const }
    : noExpiry ? { text: 'No expiry set', tone: 'red' as const }
      : r.flags.includes('over_cap') ? { text: `Longer than 90 days · ends ${fmtDate(r.expires_at)}`, tone: 'red' as const }
        : { text: `${plural(r.days_left ?? 0, 'day', 'days')} left · ${fmtDate(r.expires_at)}`, tone: r.flags.includes('expiring_soon') ? 'amber' as const : 'neutral' as const };
  const lastTerm = r.status === 'active' && r.renewals_left === 0;
  const renewals = r.renewal_count === 0 ? 'No renewals yet' : `Renewal ${r.renewal_count} of ${r.renewal_count + r.renewals_left}`;
  const ownRisk = !!me && r.owner_user_id === me;
  const src = sourceLink(r, prefix);
  const renewer = r.renewer_user_id ? nameOf(names, r.renewer_user_id) : null;
  const ask = renewer && renewerEmail
    ? <a href={`mailto:${renewerEmail}?subject=${encodeURIComponent(`GAC Pro: renew the acceptance “${r.title}”`)}&body=${encodeURIComponent(typeof location !== 'undefined' ? location.href : '')}`}>{`Ask ${firstName(renewer)} to renew`}</a>
    : null;
  let act: React.ReactNode = null;
  if (r.status === 'active') {
    if (lastTerm) {
      act = <>
        {src ? <Link className="gp-linkbtn btn-primary gp-linkbtn--compact" to={src.to} data-primary-action>{src.label}</Link> : null}
        {r.source === 'security_risk' ? <Button compact variant={src ? 'ghost' : 'primary'} onClick={onReassess} data-primary-action={src ? undefined : true} aria-label={`Re-assess · ${r.title}`}>Re-assess…</Button> : null}
      </>;
    } else if (canRenew && !ownRisk) {
      act = <Button compact onClick={onRenew} data-primary-action aria-label={`${noExpiry ? 'Set expiry' : 'Renew'} · ${r.title}`}>{noExpiry ? 'Set expiry…' : 'Renew…'}</Button>;
    } else if (renewer) {
      act = <span className="gp-ow-meta">{ownRisk ? <span>{`You own this risk · ${renewer} renews it`}</span> : <span>{`${renewer} renews it`}</span>}{ask}</span>;
    } else {
      act = <span className="gp-ow-meta"><span>Nobody else can renew it yet</span><AskAdmin slug={slug} compact verb="add a renewer" what="renews acceptances" subject={`GAC Pro: nobody can renew “${r.title}”`} /></span>;
    }
  }
  return (
    <li className="gp-ow" data-acceptance={r.key} id={domId('acc', r.key)} tabIndex={-1} data-focused={focused || undefined}>
      <span className="gp-ow-main">
        <span className="gp-ow-line">
          {src ? <Link className="gp-ow-t" to={src.to}>{r.title}</Link> : <span className="gp-ow-t">{r.title}</span>}
          <span className="gp-dim" data-part="origin">{r.origin ? 'Program point' : SOURCE_LABEL[r.source]}</span>
        </span>
        <span className="gp-ow-meta">
          <Chip text={expiry.text} tone={expiry.tone} />
          {lastTerm ? <Chip text="Last term" tone="red" /> : null}
          <span>{renewals}</span>
          <span>{`Owner ${nameOf(names, r.owner_user_id, 'unassigned')}`}</span>
        </span>
        {r.rationale ? <span className="gp-ow-meta">{r.rationale}</span> : null}
      </span>
      <span className="gp-ow-act">{act}</span>
    </li>
  );
}

function RenewDialog({ slug, acceptance, maxDays, onClose, onDone }: {
  slug: string; acceptance: CanonicalAcceptance | null; maxDays: number; onClose: () => void; onDone: (m: string, key: string) => Promise<void>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const max = new Date(Date.now() + maxDays * 86_400_000).toISOString().slice(0, 10);
  const [until, setUntil] = useState('');
  const [why, setWhy] = useState('');
  const [errs, setErrs] = useState<{ until?: string; why?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setting = !!acceptance && !acceptance.expires_at;
  return (
    <ActionDialog open={!!acceptance} title={acceptance ? `${setting ? 'Set expiry' : 'Renew'} · ${acceptance.title}` : 'Renew'} submitLabel={setting ? 'Set expiry' : 'Renew'} busy={busy} error={err}
      onClose={() => { setErr(null); setErrs({}); onClose(); }}
      onSubmit={async () => {
        if (!acceptance) return false;
        const e: typeof errs = {};
        if (!until) e.until = 'Choose the end date.';
        else if (until <= today) e.until = 'After today.';
        else if (until > max) e.until = `At most ${maxDays} days from today (${fmtDate(max)}).`;
        if (why.trim().length < 20) e.why = 'Say why it is still accepted (at least 20 characters).';
        setErrs(e);
        if (e.until || e.why) { document.getElementById(e.until ? 'renew-until' : 'renew-why')?.focus(); return false; }
        setBusy(true); setErr(null);
        try {
          await renewAcceptance(slug, acceptance.source, acceptance.acceptance_id, { expires_at: `${until}T23:59:59.000Z`, rationale: why.trim() });
          setUntil(''); setWhy('');
          await onDone(setting ? `Expires ${fmtDate(until)}` : `Renewed until ${fmtDate(until)}`, acceptance.key);
          return true;
        } catch (x) { setErr(errorText(x, 'It could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      {acceptance ? <p className="gp-dim">{setting ? 'Every acceptance expires. This sets its first expiry; it isn’t a renewal.'
        : `Renewal ${acceptance.renewal_count + 1} of ${acceptance.renewal_count + acceptance.renewals_left}${acceptance.renewals_left === 1 ? ' · the last one allowed' : ''}`}</p> : null}
      <Field id="renew-until" label="Accepted until" required hint={`Up to ${maxDays} days`} error={errs.until}>
        <Input type="date" min={today} max={max} value={until} onChange={(e) => { setUntil(e.currentTarget.value); setErrs((x) => ({ ...x, until: undefined })); }} />
      </Field>
      <Field id="renew-why" label="Why it is still accepted" required error={errs.why}>
        <Textarea rows={3} maxLength={4000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setErrs((x) => ({ ...x, why: undefined })); }} />
      </Field>
    </ActionDialog>
  );
}

/** T5-B4: a security risk on its last term is re-assessed — treated another way (mitigate / transfer / avoid) with a plan. */
function ReassessDialog({ slug, acceptance, onClose, onDone }: {
  slug: string; acceptance: CanonicalAcceptance | null; onClose: () => void; onDone: (m: string, key: string) => Promise<void>;
}) {
  const [treatment, setTreatment] = useState('');
  const [plan, setPlan] = useState('');
  const [errs, setErrs] = useState<{ treatment?: string; plan?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={!!acceptance} title={acceptance ? `Re-assess · ${acceptance.title}` : 'Re-assess'} submitLabel="Save the new treatment" busy={busy} error={err}
      onClose={() => { setErr(null); setErrs({}); setTreatment(''); setPlan(''); onClose(); }}
      onSubmit={async () => {
        if (!acceptance) return false;
        const e: typeof errs = {};
        if (!treatment) e.treatment = 'Choose how the risk is treated now.';
        if (plan.trim().length < 10) e.plan = 'Say what happens next (at least 10 characters).';
        setErrs(e);
        if (e.treatment || e.plan) { document.getElementById(e.treatment ? 'reassess-treatment' : 'reassess-plan')?.focus(); return false; }
        setBusy(true); setErr(null);
        try {
          const { risk } = await getSecurityRisk(slug, acceptance.subject.id);
          await reassessSecurityRisk(slug, risk, { treatment: treatment as 'mitigate' | 'transfer' | 'avoid', treatment_plan: plan.trim() });
          setTreatment(''); setPlan('');
          await onDone(`Re-assessed · ${treatment}`, acceptance.key);
          return true;
        } catch (x) { setErr(errorText(x, 'The re-assessment could not be saved.')); return false; }
        finally { setBusy(false); }
      }}>
      <p className="gp-dim">No renewal is left. Treat the risk another way; the acceptance closes.</p>
      <Field id="reassess-treatment" label="New treatment" required error={errs.treatment}>
        <Select value={treatment} onChange={(e) => { setTreatment(e.currentTarget.value); setErrs((x) => ({ ...x, treatment: undefined })); }}>
          <option value="">Choose…</option><option value="mitigate">Mitigate</option><option value="transfer">Transfer</option><option value="avoid">Avoid</option>
        </Select>
      </Field>
      <Field id="reassess-plan" label="What happens next" required error={errs.plan}>
        <Textarea rows={3} maxLength={4000} value={plan} onChange={(e) => { setPlan(e.currentTarget.value); setErrs((x) => ({ ...x, plan: undefined })); }} />
      </Field>
    </ActionDialog>
  );
}
